/**
 * AcceptInvitePage
 * Page for accepting organization invitations
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUser, SignIn, useAuth } from '@clerk/clerk-react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import {
  CheckCircle,
  Error as ErrorIcon,
  Business,
  Person,
} from '@mui/icons-material';
import { fetchWithAuth } from '../lib/api';

const getRoleName = (role) => {
  const names = {
    owner: 'Ιδιοκτήτης',
    admin: 'Διαχειριστής',
    manager: 'Manager',
    employee: 'Υπάλληλος',
  };
  return names[role] || role;
};

const getRoleColor = (role) => {
  const colors = {
    owner: '#7c3aed',
    admin: '#2563eb',
    manager: '#059669',
    employee: '#6b7280',
  };
  return colors[role] || '#6b7280';
};

export default function AcceptInvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { isSignedIn, isLoaded: authLoaded } = useAuth();
  const { user } = useUser();
  
  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Fetch invitation details
  useEffect(() => {
    const fetchInvitation = async () => {
      try {
        const response = await fetch(`/api/organizations/invite/${token}`);
        const data = await response.json();
        
        if (!response.ok) {
          setError(data.error || 'Η πρόσκληση δεν βρέθηκε ή έχει λήξει');
          return;
        }
        
        setInvitation(data);
      } catch (err) {
        setError('Σφάλμα φόρτωσης πρόσκλησης');
      } finally {
        setLoading(false);
      }
    };

    fetchInvitation();
  }, [token]);

  // Accept invitation
  const handleAccept = async () => {
    if (!isSignedIn) {
      return;
    }

    setAccepting(true);
    setError(null);

    try {
      const response = await fetchWithAuth(`/api/organizations/accept-invite/${token}`, {
        method: 'POST',
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        setError(data.error || 'Σφάλμα αποδοχής πρόσκλησης');
        return;
      }

      setSuccess(true);
      
      // Redirect to dashboard after 2 seconds
      setTimeout(() => {
        // Force reload to refresh organization context
        window.location.href = '/';
      }, 2000);
    } catch (err) {
      setError('Σφάλμα αποδοχής πρόσκλησης');
    } finally {
      setAccepting(false);
    }
  };

  // Show loading state
  if (loading || !authLoaded) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography>Φόρτωση πρόσκλησης...</Typography>
        </Paper>
      </Container>
    );
  }

  // Show error state
  if (error && !invitation) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Πρόβλημα με την Πρόσκληση
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            {error}
          </Typography>
          <Button variant="contained" onClick={() => navigate('/')}>
            Επιστροφή στην Αρχική
          </Button>
        </Paper>
      </Container>
    );
  }

  // Show success state
  if (success) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Καλώς ήρθατε! 🎉
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Έχετε προστεθεί επιτυχώς στον οργανισμό <strong>{invitation?.organizationName}</strong>.
          </Typography>
          <CircularProgress size={20} sx={{ mr: 1 }} />
          <Typography variant="body2" color="text.secondary">
            Ανακατεύθυνση...
          </Typography>
        </Paper>
      </Container>
    );
  }

  // Show sign-in prompt if not signed in
  if (!isSignedIn) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Business sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Πρόσκληση στον Οργανισμό
            </Typography>
            <Typography variant="h6" color="primary" gutterBottom>
              {invitation?.organizationName}
            </Typography>
            <Chip
              label={getRoleName(invitation?.role)}
              sx={{
                backgroundColor: getRoleColor(invitation?.role),
                color: 'white',
                fontWeight: 500,
              }}
            />
          </Box>
          
          <Alert severity="info" sx={{ mb: 3 }}>
            Πρέπει να συνδεθείτε για να αποδεχτείτε την πρόσκληση.
          </Alert>
          
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <SignIn 
              routing="hash"
              afterSignInUrl={`/invite/${token}`}
              afterSignUpUrl={`/invite/${token}`}
            />
          </Box>
        </Paper>
      </Container>
    );
  }

  // Show invitation details
  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper sx={{ p: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Business sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            Πρόσκληση στον Οργανισμό
          </Typography>
        </Box>

        <Box sx={{ 
          p: 3, 
          backgroundColor: '#f5f5f5', 
          borderRadius: 2,
          mb: 3 
        }}>
          <Typography variant="h5" gutterBottom sx={{ color: 'primary.main' }}>
            {invitation?.organizationName}
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Person fontSize="small" />
            <Typography variant="body1">
              Ρόλος: <strong>{getRoleName(invitation?.role)}</strong>
            </Typography>
          </Box>

          <Chip
            label={getRoleName(invitation?.role)}
            sx={{
              backgroundColor: getRoleColor(invitation?.role),
              color: 'white',
              fontWeight: 500,
            }}
          />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ 
          p: 2, 
          backgroundColor: '#e8f5e9', 
          borderRadius: 2, 
          mb: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <CheckCircle color="success" />
          <Box>
            <Typography variant="body2" fontWeight={500}>
              Συνδεδεμένος ως
            </Typography>
            <Typography variant="body1">
              {user?.primaryEmailAddress?.emailAddress}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => navigate('/')}
            disabled={accepting}
          >
            Ακύρωση
          </Button>
          <Button
            variant="contained"
            fullWidth
            onClick={handleAccept}
            disabled={accepting}
            sx={{ 
              backgroundColor: 'primary.main',
              '&:hover': { backgroundColor: 'primary.dark' }
            }}
          >
            {accepting ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                Αποδοχή...
              </>
            ) : (
              'Αποδοχή Πρόσκλησης'
            )}
          </Button>
        </Box>

        <Typography 
          variant="caption" 
          color="text.secondary" 
          sx={{ display: 'block', textAlign: 'center', mt: 2 }}
        >
          Η πρόσκληση λήγει στις {new Date(invitation?.expiresAt).toLocaleDateString('el-GR')}
        </Typography>
      </Paper>
    </Container>
  );
}

